'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { RetryGlyph, WarningGlyph } from '../../../internal/icons.js';
import { Button } from '../../actions/button/index.js';
// useLayoutEffect warns during server rendering; the effect only matters in the browser.
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const isPromise = (v) => typeof v === 'object' && v !== null && typeof v.then === 'function';
/**
 * A compact "this part didn't load" row with Try again. It is announced when
 * it appears and again when a retry fails; focus stays on Try again while the
 * retry runs and moves to `returnFocusTo` if the row goes away.
 */
export const InlineErrorRetry = forwardRef(function InlineErrorRetry({ children, onRetry, retrying, retryLabel = 'Try again', retryingMessage = 'Retrying', variant = 'inline', tone = 'danger', icon, actions, returnFocusTo, className, ...rest }, forwardedRef) {
    const [tracked, setTracked] = useState(false);
    // Incremented each time a retry settles while the row is still shown, so
    // the message is re-inserted into the alert and announced again.
    const [attempt, setAttempt] = useState(0);
    const controlled = retrying !== undefined;
    const busy = controlled ? retrying : tracked;
    const root = useRef(null);
    const mounted = useRef(true);
    const focusTarget = useRef(returnFocusTo);
    focusTarget.current = returnFocusTo;
    const setRef = useCallback((node) => {
        root.current = node;
        if (typeof forwardedRef === 'function')
            forwardedRef(node);
        else if (forwardedRef)
            forwardedRef.current = node;
    }, [forwardedRef]);
    // Controlled: a busy → idle transition while mounted is a failed retry.
    const wasBusy = useRef(busy);
    useEffect(() => {
        if (controlled && wasBusy.current && !busy)
            setAttempt((n) => n + 1);
        wasBusy.current = busy;
    }, [busy, controlled]);
    useIsoLayoutEffect(() => {
        mounted.current = true;
        const node = root.current;
        return () => {
            mounted.current = false;
            const active = typeof document === 'undefined' ? null : document.activeElement;
            if (!node || !active || !node.contains(active))
                return;
            // Our node is still in the DOM here; focus the target once the new
            // content has committed.
            setTimeout(() => {
                const t = focusTarget.current;
                const el = typeof t === 'function' ? t() : t?.current;
                el?.focus();
            }, 0);
        };
    }, []);
    const handleRetry = () => {
        if (busy)
            return;
        const result = onRetry();
        if (controlled || !isPromise(result))
            return;
        setTracked(true);
        const settle = () => {
            if (!mounted.current)
                return;
            setTracked(false);
            setAttempt((n) => n + 1);
        };
        result.then(settle, settle);
    };
    const glyph = icon === undefined ? _jsx(WarningGlyph, {}) : icon;
    return (_jsxs("div", { ref: setRef, ...rest, className: cx('mn-inline-error-retry', className), "data-variant": variant, "data-tone": tone, "data-retrying": busy || undefined, children: [glyph ? (_jsx("span", { className: "mn-inline-error-retry__icon", "aria-hidden": true, children: glyph })) : null, _jsx("span", { role: "alert", className: "mn-inline-error-retry__message", children: _jsx("span", { children: children }, attempt) }), _jsxs("span", { className: "mn-inline-error-retry__actions", children: [_jsx(Button, { size: "sm", loading: busy, leadingIcon: _jsx(RetryGlyph, {}), onClick: handleRetry, className: "mn-inline-error-retry__retry", children: retryLabel }), actions] }), _jsx("span", { role: "status", className: "mn-visually-hidden", children: busy ? retryingMessage : '' })] }));
});
//# sourceMappingURL=inline-error-retry.js.map